import CloseButton from '@/components/base/CloseButton';
import PointerEventOutside from '@/components/base/PointerEventOutside';
import DialogPanel from '@/components/base/DialogPanel';
import { createPortal } from 'react-dom';
import React, { ReactNode, useEffect, useState, RefObject, useCallback } from 'react';
import styles from './Modal.module.scss';

interface Props {
  children: ReactNode,
  dialogLabel?: string,
  dialogLabeledBy?: string,
  useAnimation?: boolean,
  modalOverrideClass?: string,
  closeButtonOverrideClass?: string,
  onClose?: () => void,
  onOpen?: () => void,
  openOnload?: boolean,
  overlayOverrideClass?: string,
  portalDestinationSelector?: string,
  toggleButtonRef?: RefObject<HTMLButtonElement|null>,
}

const Modal: React.FC<Props> =({
  children,
  dialogLabel,
  dialogLabeledBy,
  useAnimation = true,
  modalOverrideClass = '',
  closeButtonOverrideClass = '',
  onClose,
  onOpen,
  openOnload = false,
  overlayOverrideClass = '',
  portalDestinationSelector,
  toggleButtonRef,
}) => {
  const [ isModalOpen, setIsModalOpen ] = useState(openOnload);
  const [ isShowing, setIsShowing ] = useState(openOnload);
  const screenSizeSmall = 568;

  const setOverflowY = useCallback(() => {
    document.body.classList.toggle(
      'overflow-y-hidden',
      window.innerWidth <= screenSizeSmall && isModalOpen,
    );
  }, [ isModalOpen ]);

  const handleClose = useCallback(() => {
    if (isModalOpen) {
      setIsShowing(false);
      setIsModalOpen(false);
      onClose?.();

      toggleButtonRef?.current?.focus();
    }
  }, [ isModalOpen, onClose, toggleButtonRef ]);

  const handleEsc = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  }, [ handleClose ]);

  useEffect(() => {
    const toggleButtonCurrent = toggleButtonRef?.current;

    setOverflowY();

    window.addEventListener('resize', setOverflowY);
    window.addEventListener('keydown', handleEsc);

    const onToggleButtonClick = (e: Event) => {
      const newState = !isModalOpen;
      e.preventDefault();
      setIsModalOpen(newState);

      if (isModalOpen) {
        onClose?.();
      } else {
        const open = () => {
          onOpen?.();
          setIsShowing(newState);
        };
        if (useAnimation) {
          setTimeout(open, 1);
        } else {
          open();
        }
      }
    };

    if (toggleButtonCurrent?.addEventListener) {
      toggleButtonCurrent?.addEventListener('click', onToggleButtonClick);
    }

    return () => {
      window.removeEventListener('resize', setOverflowY);
      window.removeEventListener('keydown', handleEsc);

      if (toggleButtonCurrent?.removeEventListener) {
        toggleButtonCurrent?.removeEventListener('click', onToggleButtonClick);
      }
    };
  }, [
    handleClose,
    handleEsc,
    isModalOpen,
    isShowing,
    onClose,
    onOpen,
    setOverflowY,
    toggleButtonRef,
    useAnimation,
  ]);

  const buildModal = () => (<div data-component-modal-wrapper>
    <div className={`${styles.modalOverlay} ${overlayOverrideClass}`} />

    <PointerEventOutside
      inputEvents={[
        { inputEventName: 'click', handler: (e) => {
          const clickFromToggleButton = e?.target === toggleButtonRef?.current;
          if (!clickFromToggleButton && isModalOpen) {
            handleClose();
          }
        } },
      ]}
    >
      <div className={`${styles.modalBody} ${modalOverrideClass} ${isShowing ? styles.showModal : ''} ${useAnimation ? styles.useAnimation : ''}`}>
        <DialogPanel label={dialogLabel} labeledBy={dialogLabeledBy}>
          <CloseButton className={`${styles.closeBtn} ${closeButtonOverrideClass}`} handleClick={handleClose} />
          { children }
        </DialogPanel>
      </div>
    </PointerEventOutside>
  </div>);

  if (isModalOpen) {
    if (portalDestinationSelector) {
      return createPortal(buildModal(), document.querySelector(portalDestinationSelector) as Element);
    } else {
      return buildModal();
    }
  }
};

export default Modal;
